// src/lib/contactsDirectoryClient.ts
import { supabase } from "@/lib/supabase";

export type ContactAffiliation = "org_affiliated" | "vendor_partner" | "platform_global";
export type HierarchyParticipation = "full" | "none";

export type ExternalContactRow = {
  id: string;
  organization_id: string;
  location_id: string | null;
  scope: string;
  company: string | null;
  name: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  department: string | null;
  topics: unknown;
  contact_path: string | null;
  visibility_level: number;
  max_downstream_visibility_level?: number | null;
  affiliation?: ContactAffiliation | string | null;
  hierarchy_participation?: HierarchyParticipation | string | null;
  reports_to_external_id?: string | null;
  reports_to_profile_id?: string | null;
  is_active: boolean;
  created_at?: string;
};

export type UserDirectoryRow = {
  id: string;
  organization_id: string;
  profile_id: string;
  location_id: string | null;
  scope: string;
  department: string | null;
  topics: unknown;
  contact_path: string | null;
  visibility_level: number;
  max_downstream_visibility_level?: number | null;
  affiliation?: ContactAffiliation | string | null;
  hierarchy_participation?: HierarchyParticipation | string | null;
  reports_to_profile_id?: string | null;
  reports_to_external_id?: string | null;
  is_active: boolean;
  profiles?: {
    id: string;
    email: string | null;
    full_name: string | null;
    display_name: string | null;
    personal_phone?: string | null;
    work_phone?: string | null;
  } | null;
};

export type GeneralFallbackConfig = {
  columns_available: boolean;
  source: "user" | "external" | null;
  source_id: string | null;
  is_valid: boolean;
  warning: string | null;
};

export type OrgUserOption = {
  profile_id: string;
  profiles?: {
    id: string;
    email: string | null;
    full_name: string | null;
    display_name: string | null;
    personal_phone?: string | null;
    work_phone?: string | null;
  } | null;
};

export type FetchDirectoryResult = {
  ok: true;
  external: ExternalContactRow[];
  user_directory: UserDirectoryRow[];
  org_users: OrgUserOption[];
  general_fallback: GeneralFallbackConfig;
};

export type ContactImportRow = {
  name: string;
  email?: string;
  phone?: string;
  company?: string;
  title?: string;
  department?: string;
  contact_path?: string;
  visibility_level?: string | number;
  max_downstream_visibility_level?: string | number;
  location_name?: string;
  location_id?: string;
  scope?: string;
  topics?: string | string[];
  is_active?: string | boolean;
  affiliation?: string;
  hierarchy_participation?: string;
};

async function workspaceHeaders(orgId: string): Promise<HeadersInit> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (!supabase) throw new Error("Supabase not configured");
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) throw new Error("Not authenticated");
  headers.Authorization = `Bearer ${token}`;
  headers["X-Workspace-Organization-Id"] = orgId;
  return headers;
}

export async function fetchContactsDirectory(orgId: string): Promise<FetchDirectoryResult> {
  const r = await fetch("/api/admin/contacts-directory", {
    method: "GET",
    headers: await workspaceHeaders(orgId),
  });
  const j = (await r.json()) as FetchDirectoryResult & { ok?: boolean; error?: string };
  if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return {
    ok: true,
    external: j.external ?? [],
    user_directory: j.user_directory ?? [],
    org_users: j.org_users ?? [],
    general_fallback: (j.general_fallback as GeneralFallbackConfig | undefined) ?? {
      columns_available: false,
      source: null,
      source_id: null,
      is_valid: false,
      warning: "General support fallback settings are not available yet.",
    },
  };
}

export type ReportingTreeNode = {
  key: string;
  source: "user" | "external";
  row_id: string;
  profile_id: string | null;
  display_name: string | null;
  title: string | null;
  department: string | null;
  affiliation: string | null;
  hierarchy_participation: string | null;
  reports_to_profile_id: string | null;
  reports_to_external_id: string | null;
  reports_to_key: string | null;
};

export type ReportingTreeResponse = {
  ok: true;
  organization_id: string;
  nodes: ReportingTreeNode[];
  edges: Array<{ from_key: string; to_key: string }>;
  roots: string[];
  forest: unknown[];
};

export async function fetchContactsReportingTree(orgId: string): Promise<ReportingTreeResponse> {
  const r = await fetch("/api/admin/contacts-reporting-tree", {
    method: "GET",
    headers: await workspaceHeaders(orgId),
  });
  const j = (await r.json()) as ReportingTreeResponse & { ok?: boolean; error?: string };
  if (!r.ok || !j.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j as ReportingTreeResponse;
}

export async function postContactsDirectoryOp(
  orgId: string,
  body: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const r = await fetch("/api/admin/contacts-directory", {
    method: "POST",
    headers: await workspaceHeaders(orgId),
    body: JSON.stringify(body),
  });
  const j = (await r.json()) as Record<string, unknown> & { ok?: boolean; error?: string };
  if (!r.ok || j.ok === false) throw new Error((j.error as string) || `HTTP ${r.status}`);
  return j;
}

/** Minimal CSV: first row = headers; comma-separated; no quoted commas. */
export function parseSimpleContactsCsv(text: string): Record<string, string>[] {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length < 2) return [];
  const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
  return lines.slice(1).map((line) => {
    const cells = line.split(",").map((c) => c.trim());
    const row: Record<string, string> = {};
    headers.forEach((h, i) => {
      row[h] = cells[i] ?? "";
    });
    return row;
  });
}

export function csvRowsToImportPayload(rows: Record<string, string>[]): ContactImportRow[] {
  return rows.map((r) =>
    normalizeImportRow({
      name: r.name || r.full_name || "",
      email: r.email || "",
      phone: r.phone || "",
      company: r.company || "",
      title: r.title || r.job_title || "",
      department: r.department || "",
      contact_path: r.contact_path || r.path || "",
      visibility_level: r.visibility_level || r.contact_level || r.level || "1",
      max_downstream_visibility_level: r.max_downstream_visibility_level || r.downstream_visibility_level || "",
      location_name: r.location_name || r.office || r.location || "",
      location_id: r.location_id || "",
      scope: r.scope || "org",
      topics: r.topics || "",
      is_active: r.is_active === "0" || r.is_active?.toLowerCase() === "false" ? "false" : "true",
      affiliation: r.affiliation || "",
      hierarchy_participation: r.hierarchy_participation || "",
    })
  );
}

function normalizeImportRow(row: Record<string, unknown>): ContactImportRow {
  return {
    name: String(row.name ?? row.full_name ?? "").trim(),
    email: String(row.email ?? "").trim(),
    phone: String(row.phone ?? "").trim(),
    company: String(row.company ?? "").trim(),
    title: String(row.title ?? row.job_title ?? "").trim(),
    department: String(row.department ?? "").trim(),
    contact_path: String(row.contact_path ?? row.path ?? "").trim(),
    visibility_level: (row.visibility_level ?? row.contact_level ?? row.level ?? "1") as
      | string
      | number,
    max_downstream_visibility_level: (row.max_downstream_visibility_level ??
      row.downstream_visibility_level ??
      "") as string | number,
    location_name: String(row.location_name ?? row.office ?? row.location ?? "").trim(),
    location_id: String(row.location_id ?? "").trim(),
    scope: String(row.scope ?? "org").trim(),
    topics: (row.topics ?? "") as string | string[],
    is_active: (row.is_active ?? true) as string | boolean,
    affiliation: String(row.affiliation ?? "").trim(),
    hierarchy_participation: String(row.hierarchy_participation ?? "").trim(),
  };
}

export function parseSimpleContactsJson(text: string): ContactImportRow[] {
  const parsed = JSON.parse(text) as unknown;
  if (Array.isArray(parsed)) {
    return parsed
      .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
      .map(normalizeImportRow);
  }
  if (parsed && typeof parsed === "object") {
    const candidate = parsed as Record<string, unknown>;
    if (Array.isArray(candidate.rows)) {
      return candidate.rows
        .filter((row): row is Record<string, unknown> => !!row && typeof row === "object")
        .map(normalizeImportRow);
    }
  }
  throw new Error("JSON import must be an array of contact rows or an object with a rows array.");
}
